const React = require('react')
const { PathProvider } = require('./utils/pathContext')

/**
 * Shared `wrapPageElement` for BOTH `gatsby-ssr.js` and `gatsby-browser.js`.
 *
 * Gatsby has two separate entry points for this hook, and a provider registered in only one of them
 * exists in only that environment. `gatsby-ssr.js` used to declare this inline with no browser
 * counterpart, so `PathContext` was populated during server rendering and left at its
 * `createContext('/')` default in the browser. `useIsChinaSite()` therefore returned true on the
 * server for `/cn/…` paths and always false on the client.
 *
 * Three components branch on that value, and each of them REMOVES Vimeo content when it is true:
 *
 *   videoPlayer/index.tsx:189   if (isChinaSite && type === 'vimeo') return null
 *   videoCta/videoCta.tsx:65    if (isChinaSite && type === 'vimeo') return null
 *   rawHTML/index.tsx:56        isChinaSite && rawHtml ? stripVimeoIframes(rawHtml) : rawHtml
 *
 * Two consequences of the missing browser half, both live on production today:
 *
 * 1. The China protection does not work client-side. The server correctly omits the Vimeo player,
 *    then React re-adds it in the browser — so visitors on uipath.com.cn wait on a host China
 *    blocks. React 17 patches the difference silently, which is why it went unnoticed.
 *
 * 2. Under React 18 the same difference is a hydration mismatch. Measured on
 *    /cn/solutions/industry/banking-automation/: the server rendered 159 nodes inside
 *    LoadingContainer, the client 189 — the extra ones being VideoPlayer__Image-File and its
 *    wrappers. Because the player sits inside a carousel with cloned slides the mismatch repeats,
 *    producing 29 × #418 and then #423, which discards the server-rendered tree entirely.
 *
 * Keeping one implementation here and re-exporting it from both entry points is what Gatsby's docs
 * recommend, and it removes the possibility of the two halves drifting apart again.
 */
exports.wrapPageElement = ({ element, props }) => {
  const pathname = (props && props.location && props.location.pathname) || '/'
  // The path the page was built at (see usePagePath). The browser passes it as
  // pageResources.page.path; on the server there are no pageResources, but there `location` is
  // the page being built, so its pathname is that same path.
  const pagePath =
    (props && props.pageResources && props.pageResources.page && props.pageResources.page.path) ||
    pathname
  return React.createElement(PathProvider, { pathname, pagePath }, element)
}
